import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { TranslationMapper, TranslationData } from './clientTranslationtypes';

// GET translation mappers
export async function fetchTranslationMappers({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  searched,
  code,
  clientId,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  searched?: boolean;
  code?: string;
  clientId?: string | number;
}): Promise<TranslationMapper[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
  };

  // Add optional search parameters
  if (searched !== undefined) params.searched = searched;
  if (code) params.code = code;
  if (clientId) params.clientId = clientId;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.LIST);
  return api.get<TranslationMapper[]>(endpoint, params);
}

// GET translation mappers count
export async function fetchTranslationMappersCount({
  clientCode,
  searched,
  code,
  clientId,
}: {
  clientCode: string;
  searched?: boolean;
  code?: string;
  clientId?: string | number;
}): Promise<number> {
  const params: Record<string, any> = {};

  // Add optional search parameters
  if (searched !== undefined) params.searched = searched;
  if (code) params.code = code;
  if (clientId) params.clientId = clientId;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.COUNT);
  const response = await api.get<any>(endpoint, params);
  
  // Handle different response formats
  if (typeof response === 'number') {
    return response;
  } else if (response && typeof response.count === 'number') {
    return response.count;
  }
  
  logger.warn('Invalid count response:', response);
  return 0;
}

// GET all translation mapper codes (for dropdown display)
// Calls: /translationMapper/?firstResult=-1&maxResult=0
export async function fetchAllTranslationMapperCodes({
  clientCode,
}: {
  clientCode: string;
}): Promise<TranslationMapper[]> {
  const params: Record<string, any> = {
    firstResult: -1,
    maxResult: 0,
  };

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.ALL_CODES);
  return api.get<TranslationMapper[]>(endpoint, params);
}

// GET client translation data with search (for search without input/selection)
// Calls: /clientTranslation/?firstResult=0&maxResult=10&searched=true
export async function fetchClientTranslationWithSearch({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  code,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: string | number;
  code?: string;
}): Promise<TranslationData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched: true,
  };

  // Add filter parameters if provided
  if (clientId) params.clientId = clientId;
  if (code) params.code = code;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.DATA);
  return api.get<TranslationData[]>(endpoint, params);
}

// GET client translation count with search (for search without input/selection)
// Calls: /clientTranslation/count?&searched=true
export async function fetchClientTranslationCountWithSearch({
  clientCode,
  clientId,
  code,
}: {
  clientCode: string;
  clientId?: string | number;
  code?: string;
}): Promise<number> {
  const params: Record<string, any> = {
    searched: true,
  };

  // Add filter parameters if provided
  if (clientId) params.clientId = clientId;
  if (code) params.code = code;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.DATA_COUNT);
  const response = await api.get<any>(endpoint, params);
  
  // Handle different response formats
  if (typeof response === 'number') {
    return response;
  } else if (response && typeof response.count === 'number') {
    return response.count;
  }
  
  logger.warn('Invalid count response:', response);
  return 0;
}

// GET translation data by mapper ID or code/clientId
export async function fetchTranslationData({
  clientCode,
  mapperId,
  code,
  clientId,
  firstResult = 0,
  maxResult = 10,
  searched = true,
}: {
  clientCode: string;
  mapperId?: number;
  code?: string;
  clientId?: number;
  firstResult?: number;
  maxResult?: number;
  searched?: boolean;
}): Promise<TranslationData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  // Add optional parameters only if they exist
  if (mapperId) params.mapperId = mapperId;
  if (code) params.code = code;
  if (clientId) params.clientId = clientId;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.DATA);
  return api.get<TranslationData[]>(endpoint, params);
}

// GET translation data count
export async function fetchTranslationDataCount({
  clientCode,
  mapperId,
  code,
  clientId,
  searched = true,
}: {
  clientCode: string;
  mapperId?: number;
  code?: string;
  clientId?: number;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  // Add optional parameters only if they exist
  if (mapperId) params.mapperId = mapperId;
  if (code) params.code = code;
  if (clientId) params.clientId = clientId;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.DATA_COUNT);
  const response = await api.get<{ count: number }>(endpoint, params);
  return response.count;
}

// CREATE translation mapper
export async function createTranslationMapper({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<TranslationMapper> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT_TRANSLATION.CREATE);
  return api.post<TranslationMapper>(endpoint, payload);
}

// UPDATE translation mapper
export async function updateTranslationMapper({
  clientCode,
  mapperId,
  payload,
}: {
  clientCode: string;
  mapperId: number;
  payload: any;
}): Promise<TranslationMapper> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.CLIENT_TRANSLATION.UPDATE}${mapperId}`);
  return api.put<TranslationMapper>(endpoint, payload);
}

// DELETE translation mapper
export async function deleteTranslationMapper({
  clientCode,
  mapperId,
}: {
  clientCode: string;
  mapperId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.CLIENT_TRANSLATION.DELETE}${mapperId}`);
  return api.delete(endpoint);
}

// CREATE translation data
export async function createTranslationData({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: {
    code: string;
    clientId: number;
    key: string;
    value: string;
    keyDescription: string;
    valueDescription: string;
  };
}): Promise<{ success: boolean; object: TranslationData; messages: string[] }> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.TRANSLATION.CREATE_CLIENT_TRANSLATION);
  return api.post<{ success: boolean; object: TranslationData; messages: string[] }>(endpoint, payload);
}

// UPDATE translation data
export async function updateTranslationData({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: TranslationData;
}): Promise<TranslationData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.TRANSLATION.UPDATE_CLIENT_TRANSLATION);
  return api.put<TranslationData>(endpoint, payload);
}

// DELETE translation data using POST with deleteWithBigInteger endpoint
export async function deleteTranslationData({
  clientCode,
  translationData,
}: {
  clientCode: string;
  translationData: TranslationData;
}): Promise<{ success: boolean; object: any; messages: string[] }> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.TRANSLATION.DELETE_CLIENT_TRANSLATION);
  return api.post<{ success: boolean; object: any; messages: string[] }>(endpoint, translationData);
}
